import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import LookupsAPI from "../../API/lookups.api";
import StepsCard from "../../components/Cards/StepsCard";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import SelectProducts from "../../components/PublishBundleSteps/SelectProducts";
import ManageModel from "../../components/PublishModelSteps/ManageModel";
import ManageSubscriptions from "../../components/PublishModelSteps/ManageSubscriptions";
import PreviewProduct from "../../components/PublishModelSteps/PreviewProduct";
import MultistepCompletionHandler from "../../components/Handlers/MultistepCompletionHandler";
import PublishedModelsAPI from "../../API/publishedModels.api";
import {
    BOOLEAN_DROPDOWN,
    CURRENCY,
    MODEL_SCOPE,
    PRODUCT_TYPES,
    SUBSCRIPTION_TYPES,
    TRIAL_DAYS,
} from "../../utils/constants";
import {
    getCountriesDropdown,
    reformatCountry,
} from "../../utils/CountryHelper";
import { isEmpty } from "lodash";
import MainHeader from "../../components/Headers/MainHeader";
import { ErrorAlert } from "../../utils/Alerts";
import ProductVersionAPI from "../../API/productVersion.api";

const PublishBundleForm = ({ isUpdate }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [productId, setProductId] = useState(null);
    const [fetchedProductVersion, setFetchedProductVersion] = useState({});

    //Step1
    const [acceptedProducts, setAcceptedProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [materialNames, setMaterialNames] = useState([]);

    //Step2
    const [countries, setCountries] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [supportedAccessories, setSupportedAccessories] = useState([]);
    const [scannerTypes, setScannerTypes] = useState([]);
    const [tags, setTags] = useState([]);
    const [productImages, setProductImages] = useState([]);
    const [coverImage, setCoverImage] = useState([]);
    const [productGuide, setProductGuide] = useState({});
    const [isForceUpgradeEnabled, setIsForceUpgradeEnabled] = useState(false);
    const [manageModelForm, setManageModelForm] = useState({
        productName: "",
        materialName: "",
        description: "",
        compatibleWith: [],
        supportedAccessories: [],
        industries: [],
        tags: [],
        productVersion: "",
        whatsNew: [],
        universalModel: "",
        supportedCountries: [],
        images: [],
        instructions: [],
        forceUpgrade: BOOLEAN_DROPDOWN.false,
    });

    //Step3
    const [manageSubsciptionsForm, setManageSubsciptionsForm] = useState({
        solo: "",
        hasTrial: true,
        trialDays: TRIAL_DAYS[7],
        currency: null,
        choosenSubscriptionTypes: {
            monthlySubscription: null,
            annualSubscription: null,
        },
    });

    //Step 5
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const bundleId = location.state?.publishedModelId;
    const versionId = location.state?.versionId;

    const STEPS = [
        { number: "1", name: t("publishBundleStep1") },
        { number: "2", name: t("publishBundleStep2") },
        { number: "3", name: t("publishBundleStep3") },
        { number: "4", name: t("publishModelStep4") },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/published-products",
            element: () => (
                <Trans i18nKey="publishedProducts">Published Products</Trans>
            ),
        },
        {
            element: () => getProperTitle(),
        },
    ];

    const finalStepTitles = {
        isSubmitting: t("publishRequestCreationSubmittingTitle"),
        isSubmitted: t("publishRequestSubmitSuccessTitle"),
        default: t("publishRequestSubmitFailTitle"),
    };

    const finalStepContent = {
        isSubmitting: t("publishRequestSubmittingMessage"),
        isSubmitted: t("publishRequestSubmitSuccessMessage"),
        default: (
            <span>
                <Trans i18nKey="publishRequestSubmitFailMessage">
                    Your request could not be uploaded. Please check your
                    internet connection and try again. if you still face any
                    issue please contact us at
                </Trans>
                <b> Support@si-ware.com</b>
            </span>
        ),
    };

    useEffect(() => {
        getAcceptedProducts();
        getCountriesDropdown(setCountries);
        fetchIndustriesLookup();
        fetchSupportedAccessoriesLookup();
        fetchScannerTypes();
        fetchTags();
    }, []);

    useEffect(() => {
        if (selectedProducts && fetchedProductVersion.bundleProducts) {
            const selectedProductIds = selectedProducts.map(
                (product) => product.productId
            );
            const fetchedProductIds = fetchedProductVersion.bundleProducts.map(
                (product) => product.productId
            );

            setIsForceUpgradeEnabled(
                fetchedProductIds.every((paramId) =>
                    selectedProductIds.includes(paramId)
                )
            );
        }
    }, [selectedProducts]);

    useEffect(() => {
        if (
            bundleId &&
            !isEmpty(acceptedProducts) &&
            isEmpty(fetchedProductVersion) &&
            manageModelForm.productName === ""
        ) {
            fetchProductById(bundleId);
        }
    }, [location, acceptedProducts]);

    useEffect(() => {
        setAcceptedProducts((prevAccepted) =>
            prevAccepted.map((acceptedProduct) => {
                return {
                    ...acceptedProduct,
                    isDisabled: materialNames.includes(
                        acceptedProduct.materialName
                    ),
                };
            })
        );
    }, [selectedProducts, materialNames]);

    const nextStep = () => {
        setCurrentStep((prev) => prev + 1);
    };

    const prevStep = () => {
        setCurrentStep((prev) => prev - 1);
    };

    const mapMaterialNameToProducts = (products) => {
        return products?.map((product) => {
            return {
                ...product,
                label: (
                    <span className="flex items-center gap-1">
                        {product.label}
                        <span className="text-xxs text-primary-color-orange">
                            ({product.materialName})
                        </span>
                    </span>
                ),
                searchable: product.searchable || product.label,
                isDisabled: false,
            };
        });
    };

    const getAcceptedProducts = async () => {
        const result = await LookupsAPI.getAcceptedProducts();
        setAcceptedProducts(mapMaterialNameToProducts(result));
    };

    const fetchSupportedAccessoriesLookup = async () => {
        const result = await LookupsAPI.getSupportedAccessories();
        setSupportedAccessories(result);
    };

    const fetchIndustriesLookup = async () => {
        const result = await LookupsAPI.getIndustries();
        setIndustries(result);
    };

    const fetchScannerTypes = async () => {
        const result = await LookupsAPI.getScannerTypes();
        setScannerTypes(result);
    };

    const fetchTags = async () => {
        const result = await LookupsAPI.getTags();
        setTags(result);
    };

    const fetchProductById = async (id) => {
        const result = await PublishedModelsAPI.getPublishedProductById(
            id,
            versionId
        );
        if (isEmpty(result)) {
            ErrorAlert(t("loadProductVersionFailMessage"));
            history.push("/published-products");
        } else {
            mapExistingData(result);
            setFetchedProductVersion(result);
        }
    };

    const getPriceFromPlan = (plans = [], type, currency) => {
        return (
            plans?.find(
                (plan) =>
                    plan.durationType === type &&
                    plan.currency?.abbreviation === currency
            )?.price || null
        );
    };

    const mapChangedProductName = (products) => {
        const acceptedProductDict = {};

        acceptedProducts?.forEach((product) => {
            acceptedProductDict[product.productId] = product;
        });

        const getIsOutdatedLabel = (product) => {
            const currentAcceptedProduct =
                acceptedProductDict[product.productId];

            const accpetedProductName =
                currentAcceptedProduct.searchable ||
                currentAcceptedProduct.label;

            const mainName = product.searchable || product.label;

            return (
                currentAcceptedProduct &&
                accpetedProductName !== mainName && (
                    <div className="flex items-center gap-2">
                        <i className="fas fa-arrow-right"></i>
                        {accpetedProductName}
                    </div>
                )
            );
        };

        return products?.map((product) => {
            return {
                ...product,
                label: (
                    <span className="flex items-center gap-1">
                        {product.label}
                        {getIsOutdatedLabel(product)} -
                        <span className="text-xxs">{product.version}</span>
                        <span className="text-xxs text-primary-color-orange">
                            ({product.materialName})
                        </span>
                    </span>
                ),
                searchable: product.searchable || product.label,
                isDisabled: false,
            };
        });
    };

    const mapExistingData = (bundle) => {
        if (!isEmpty(bundle)) {
            const subscriptionPlans = bundle.subscriptionPlans;

            setProductId(bundle.id);

            setSelectedProducts(mapChangedProductName(bundle.bundleProducts));

            setManageModelForm((prevForm) => ({
                ...prevForm,
                ...(isUpdate && { productVersion: bundle.version }),
                ...(isUpdate && { whatsNew: bundle.whatsNew }),
                productName: bundle.productName,
                description: bundle.description,
                compatibleWith: bundle.compatibleWith,
                supportedAccessories: bundle.supportedAccessories,
                industries: bundle.industries,
                tags: bundle.tags,
                universalModel: {
                    value: bundle.universalModel
                        ? MODEL_SCOPE.global
                        : MODEL_SCOPE.local,
                    booleanValue: bundle.universalModel,
                },
                supportedCountries: bundle.supportedCountries.map((country) =>
                    reformatCountry(country)
                ),
                forceUpgrade:
                    BOOLEAN_DROPDOWN[bundle.forceUpgrade] ||
                    BOOLEAN_DROPDOWN.false,
                instructions: bundle.instructions,
            }));

            setMaterialNames(bundle.materialNames);

            setCoverImage(bundle.coverImage ? [bundle.coverImage] : []);

            setProductImages(bundle.images);

            setManageSubsciptionsForm((prevForm) => ({
                ...prevForm,
                hasTrial: bundle.hasTrial,
                trialDays: TRIAL_DAYS[bundle.trialDays],
                isMonthlyRequired: bundle.isMonthlyRequired,
                isAnnualRequired: bundle.isAnnualRequired,
                choosenSubscriptionTypes: {
                    annualSubscription: subscriptionPlans?.some(
                        (plan) =>
                            plan.durationType === SUBSCRIPTION_TYPES.annual
                    ),
                    monthlySubscription: subscriptionPlans?.some(
                        (plan) =>
                            plan.durationType === SUBSCRIPTION_TYPES.monthly
                    ),
                },
                annualSubscriptionDollar: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.annual,
                    CURRENCY.dollar
                ),

                annualSubscriptionEuro: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.annual,
                    CURRENCY.euro
                ),

                monthlySubscriptionDollar: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.monthly,
                    CURRENCY.dollar
                ),

                monthlySubscriptionEuro: getPriceFromPlan(
                    subscriptionPlans,
                    SUBSCRIPTION_TYPES.monthly,
                    CURRENCY.euro
                ),
            }));
        }
    };

    const getIdMappedArray = (propName) => {
        const map = {};
        fetchedProductVersion[propName]?.forEach((prop) => {
            map[prop.id || prop.value || prop] = prop;
        });
        return map;
    };

    const mapIsDeletedObjects = (dict) => {
        return (
            dict &&
            Object.values(dict).map((val) => ({
                id: val.id || val.value,
                isDeleted: true,
            }))
        );
    };

    const getUpdateObjectValues = (arr, propName, extraProps = () => {}) => {
        const map = getIdMappedArray(propName);
        const mappedArr = arr.map((item) => {
            const itemId = item.id || item.value;
            const alreadyExists = !!map[itemId];
            delete map[itemId];
            return { id: itemId, ...extraProps(item, alreadyExists) };
        });
        const deletedItems = mapIsDeletedObjects(map) || [];

        return [...mappedArr, ...deletedItems];
    };

    const sanitizeCreatableSelectPayload = (arr, dict) => {
        return arr.map((record) => {
            const recordName = record.name || record.label;
            const recordId = record.id || record.value;
            dict && delete dict[recordId];
            if (recordName && record.isCreated) {
                return {
                    name: recordName,
                    isCreated: record.isCreated,
                };
            } else if (recordId) {
                return {
                    id: recordId,
                };
            }
        });
    };

    const sanitizeUniqueCreatableSelect = (obj, propName) => {
        const map = getIdMappedArray(propName);
        const arr = obj[propName];
        const sanitizedArr = sanitizeCreatableSelectPayload(arr, map);
        const addedDeleteTags = mapIsDeletedObjects(map) || [];

        return [...sanitizedArr, ...addedDeleteTags];
    };

    const getObjectValues = (_object) => {
        return _object && _object.map((obj) => obj.value);
    };

    const reformatFormData = () => {
        let reformattedData = {
            productVersionIds: selectedProducts.map((product) => product.value),
            ...manageModelForm,
            coverImage: manageModelForm.coverImage[0],
            productGuideURI: manageModelForm.productGuideURI,
            compatibleWith: getObjectValues(manageModelForm.compatibleWith),
            supportedAccessories: getObjectValues(
                manageModelForm.supportedAccessories
            ),
            universalModel: manageModelForm.universalModel?.booleanValue,
            supportedCountries: getObjectValues(
                manageModelForm.supportedCountries
            ),
            type: PRODUCT_TYPES.bundle,
            solo: true,
            hasTrial: manageSubsciptionsForm.hasTrial,
            ...(manageSubsciptionsForm.hasTrial && {
                trialDays: manageSubsciptionsForm.trialDays?.value,
            }),
            forceUpgrade:
                manageModelForm.forceUpgrade?.booleanValue ||
                BOOLEAN_DROPDOWN.false.value,
            subscriptionPlans: [],
        };

        if (reformattedData?.subscriptionPlans) {
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.monthlySubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 1,
                });

                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 2,
                });
            }
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.annualSubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 1,
                });
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 2,
                });
            }
        }

        if (bundleId) {
            reformattedData = { ...reformattedData, productId };
        }

        // delete reformattedData.enforceoutlierdetection;
        return reformattedData;
    };

    const isProductIdsUpdated = () => {
        if (
            fetchedProductVersion.bundleProducts.length !==
            selectedProducts.length
        )
            return true;

        const fetchedDataMap = {};
        fetchedProductVersion.bundleProducts.forEach(
            (product) => (fetchedDataMap[product.value] = product)
        );

        for (const product of selectedProducts) {
            if (fetchedDataMap[product.value]) {
                delete fetchedDataMap[product.value];
            } else {
                return true;
            }
        }

        return !!fetchedDataMap.length;
    };

    const getUpdatePayload = () => {
        let reformattedData = {
            id: versionId,
            productVersionIds: isProductIdsUpdated()
                ? selectedProducts.map((product) => product.value)
                : undefined,
            ...manageModelForm,
            images: getUpdateObjectValues(
                productImages,
                "images",
                (item, exists) =>
                    exists ? {} : { image: item.preview, isCreated: true }
            ),
            industries: sanitizeUniqueCreatableSelect(
                manageModelForm,
                "industries"
            ),
            tags: sanitizeUniqueCreatableSelect(manageModelForm, "tags"),
            coverImage: manageModelForm.coverImage[0],
            productGuideURI: manageModelForm.productGuideURI,
            scannerTypes: getUpdateObjectValues(
                manageModelForm.compatibleWith,
                "compatibleWith"
            ),
            version: manageModelForm.productVersion,
            supportedAccessories: getUpdateObjectValues(
                manageModelForm.supportedAccessories,
                "supportedAccessories"
            ),
            universalModel: manageModelForm.universalModel?.booleanValue,
            supportedCountries: manageModelForm.universalModel?.booleanValue
                ? []
                : getUpdateObjectValues(
                      manageModelForm.supportedCountries,
                      "supportedCountries"
                  ),
            type: PRODUCT_TYPES.bundle,
            solo: true,
            trialDays: manageSubsciptionsForm.hasTrial
                ? manageSubsciptionsForm?.trialDays?.value
                : undefined,

            subscriptionPlans: [],
            hasTrial: manageSubsciptionsForm.hasTrial,
            forceUpgrade:
                manageModelForm.forceUpgrade?.booleanValue ||
                BOOLEAN_DROPDOWN.false.value,
        };

        if (reformattedData?.subscriptionPlans) {
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.monthlySubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 1,
                });

                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.monthlySubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.monthly,
                    currencyId: 2,
                });
            }
            if (
                manageSubsciptionsForm?.choosenSubscriptionTypes
                    ?.annualSubscription
            ) {
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionDollar,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 1,
                });
                reformattedData?.subscriptionPlans?.push({
                    price: manageSubsciptionsForm.annualSubscriptionEuro,
                    durationType: SUBSCRIPTION_TYPES.annual,
                    currencyId: 2,
                });
            }
        }

        delete reformattedData.compatibleWith;
        delete reformattedData.productVersion;

        return reformattedData;
    };

    const getProperSubmitAction = () => {
        if (!bundleId) return publishBundle;

        return isUpdate ? updateModelVersion : createNewVersion;
    };

    const getProperTitle = () => {
        if (!bundleId) return t("publishProduct");

        return isUpdate ? t("editVersion") : t("newVersion");
    };

    const publishBundle = async () => {
        setIsSubmitting(true);
        const result = await PublishedModelsAPI.publishBundle(
            reformatFormData()
        );
        if (result) {
            setIsSubmitted(true);
        }
        setIsSubmitting(false);
    };

    const createNewVersion = async () => {
        setIsSubmitting(true);
        const result = await ProductVersionAPI.createProductVersion(
            reformatFormData()
        );
        if (result?.inserted) {
            setIsSubmitted(true);
        }
        setIsSubmitting(false);
    };

    const updateModelVersion = async () => {
        setIsSubmitting(true);
        const result = await ProductVersionAPI.updateProductVersion(
            getUpdatePayload()
        );
        if (result?.modified) {
            setIsSubmitted(true);
        }
        setIsSubmitting(false);
    };

    const renderForm = () => {
        switch (currentStep) {
            case 1:
                return (
                    <SelectProducts
                        selectedProducts={selectedProducts}
                        setSelectedProducts={setSelectedProducts}
                        setMaterialNames={setMaterialNames}
                        acceptedProducts={acceptedProducts}
                        nextStep={nextStep}
                        nextStepButtonText={t("publishBundleStep2")}
                    />
                );
            case 2:
                return (
                    <ManageModel
                        nextStepButtonText={t("publishBundleStep3")}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        manageModelForm={manageModelForm}
                        setManageModelForm={setManageModelForm}
                        // selectedModel,
                        // selectedModelMaterial,
                        // selectedParameterVersion,
                        supportedAccessories={supportedAccessories}
                        countries={countries}
                        industries={industries}
                        scannerTypes={scannerTypes}
                        tags={tags}
                        productImages={productImages}
                        setProductImages={setProductImages}
                        coverImage={coverImage}
                        setCoverImage={setCoverImage}
                        productGuide={productGuide}
                        setProductGuide={setProductGuide}
                        isBundle={true}
                        selectedProductsMaterials={materialNames}
                        isNewVersion={bundleId}
                        productId={productId}
                        product={fetchedProductVersion}
                        isUpdate={isUpdate}
                        isForceUpgradeEnabled={isForceUpgradeEnabled}
                    />
                );
            case 3:
                return (
                    <ManageSubscriptions
                        nextStepButtonText={t("publishModelStep4")}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        manageSubsciptionsForm={manageSubsciptionsForm}
                        setManageSubsciptionsForm={setManageSubsciptionsForm}
                        isBundle={true}
                    />
                );
            case 4:
                return (
                    <PreviewProduct
                        product={{
                            ...manageModelForm,
                            materialName: materialNames,
                        }}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        publishModel={getProperSubmitAction()}
                        bundleProducts={selectedProducts}
                        manageSubsciptionsForm={manageSubsciptionsForm}
                        isBundle={true}
                    />
                );
            case 5:
                return (
                    <MultistepCompletionHandler
                        isSubmitting={isSubmitting}
                        isSubmitted={isSubmitted}
                        prevStep={prevStep}
                        titles={finalStepTitles}
                        content={finalStepContent}
                        successRedirect={() => history.goBack()}
                    />
                );
            default:
                return <div></div>;
        }
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <MainHeader header={getProperTitle()} />
            <StepsCard
                steps={STEPS}
                activeNumber={currentStep}
                className="mb-4"
            />
            {renderForm()}
        </>
    );
};

export default PublishBundleForm;
